import React, { useState, useEffect } from "react";
import {
    Row, Col, Form, FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText,
    TabContent, TabPane, Nav, NavItem, NavLink,
    Button
} from 'reactstrap';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import PaymentTable from '../Payment/PaymentTable';
import FeeComments from './FeeComments';
import { store, getCurrency, getShortDate, toggleOptionsMenu } from '../../Helper';
import { getFee, deleteFee, deletePayment, getPayments } from '../../API';
import YesNoModal from '../YesNoModal';

function Fee(props) {
    const [activeTab, setActiveTab] = useState('payments');
    const [fee, setFee] = useState(null);
    const [isLoadingFee, setIsLoadingFee] = useState(true);
    const [payments, setPayments] = useState([]);
    const [isLoadingPayments, setIsLoadingPayments] = useState(true);
    const [deletionConfirmationModal, setDeletionConfirmationModal] = useState(false);

    useEffect(() => {
        fetchFee();
        fetchPayments();
    }, []);

    const fetchFee = async () => {
        setIsLoadingFee(true);

        const fee = await getFee(props.match.params.feeId);
        setFee(fee);

        setIsLoadingFee(false);
    };

    const fetchPayments = async () => {
        setIsLoadingPayments(true);

        const paymentAPIResults = await getPayments({ FeeIDs: [props.match.params.feeId] });
        setPayments(paymentAPIResults.sort((a, b) => (a.paidDate < b.paidDate) ? 1 : ((b.paidDate < a.paidDate) ? -1 : 0)));

        setIsLoadingPayments(false);
    };

    const toggleDeletionConfirmationModal = () => setDeletionConfirmationModal(!deletionConfirmationModal);

    const deleteRecord = async () => {
        if (await deleteFee(fee.feeID))
            props.history.push('/admin/fees');
        else
            console.log('Unable to delete this record.');
    };

    const deletePaymentRecord = async (paymentId) => {
        if (await deletePayment(paymentId))
            await fetchPayments();
        else
            console.log('Unable to delete this record.');
    };

    const toggle = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    let feeJSX = (<div>No Fee Found...</div>);
    let paymentsJSX = null;
    if (isLoadingFee) {
        feeJSX = (<div>Loading...</div>);
    } else if (fee) {
        const contactName = `${fee.contactFirstName} ${fee.contactLastName}`;
        const city = `${fee.city}, ${fee.state} ${fee.zip}`;
        const paidInFull = fee.isPaidInFull ? "Paid" : "Unpaid";
        const paidInFullIcon = fee.isPaidInFull ? (<i className="fas fa-check-square"></i>) : (<i className="far fa-square" ></i >);

        const editURL = '/admin/fees/' + fee.feeID + '/edit';

        feeJSX = (
            <div className="item-details">
                <div className="properties">
                    <div className="svg-label tooltip-parent">
                        {paidInFullIcon}
                        <span>{paidInFull}</span>
                        <div className="tooltip-target">
                            Paid In Full
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-asterisk"></i>
                        <span>{fee.account.accountNumber}</span>
                        <div className="tooltip-target">
                            Account Number
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-city"></i>
                        <span>{fee.account.name}</span>
                        <div className="tooltip-target">
                            Account
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-users"></i>
                        <span>{fee.division?.divisionName}</span>
                        <div className="tooltip-target">
                            DANR Program
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-industry"></i>
                        <span>{fee.facility}</span>
                        <div className="tooltip-target">
                            Facility
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-hashtag"></i>
                        <span>{fee.identifier}</span>
                        <div className="tooltip-target">
                            EPA ID/Permit Number
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-file-alt"></i>
                        <span>{fee.feeDescription}</span>
                        <div className="tooltip-target">
                            Description
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="far fa-calendar"></i>
                        <span>{fee.fiscalYear}</span>
                        <div className="tooltip-target">
                            Fiscal Year
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-dollar-sign"></i>
                        <span>{getCurrency(fee.feeAmount)}</span>
                        <div className="tooltip-target">
                            Fee Amount
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="fas fa-dollar-sign"></i>
                        <span>{getCurrency(fee.currentBalance)}</span>
                        <div className="tooltip-target">
                            Balance
                        </div>
                    </div>
                    <div className="svg-label tooltip-parent">
                        <i className="far fa-calendar-alt"></i>
                        <span>{getShortDate(fee.dueDate)}</span>
                        <div className="tooltip-target">
                            Due Date
                        </div>
                    </div>
                </div>
                <div className="actions">
                    {window.env.user.userRoles.some(ur => [1, 2].includes(ur.roleID)) ? (
                        <Link to={editURL} className="action-icon action-edit">
                            <i className="fas fa-edit"></i>
                        </Link>
                    ) : null}
                    <div onClick={toggleOptionsMenu} className="action-icon action-dropdown">
                        <i className="fas fa-ellipsis-h"></i>
                        <div className="action-dropdown-menu">
                            <Link className="action-dropdown-menu-item" to={`/admin/accounts/${fee.accountID}`}>View Account</Link>
                            {window.env.user.userRoles.some(ur => [1, 2].includes(ur.roleID)) ? (
                                <div className="action-dropdown-menu-item" onClick={toggleDeletionConfirmationModal}>Delete</div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );

        if (isLoadingPayments) {
            paymentsJSX = (<div>Loading...</div>);
        } else {
            paymentsJSX = (<PaymentTable payments={payments} feeId={fee.feeID} deleteRecord={deletePaymentRecord}/>);
        }
    }

    return (
        <div id="fee">
            <Row className="title-row">
                <Col>
                    <h1>{fee?.account ? fee.account.name + ' Fee' : 'Fee'}</h1>
                </Col>
            </Row>
            <Row className="details-row">
                <Col>
                    {feeJSX}
                </Col>
            </Row>

            <Row className="tabs-row">
                <Col>
                    <Nav tabs>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'payments' })}
                                onClick={() => { toggle('payments'); }}>
                                Payments
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === 'comments' })}
                                onClick={() => { toggle('comments'); }}>
                                Comments
                            </NavLink>
                        </NavItem>
                    </Nav>
                </Col>
            </Row>
            <Row>
                <Col>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="payments">
                            {paymentsJSX}
                        </TabPane>
                        <TabPane tabId="comments">
                            {fee ? <FeeComments defaultSearch={{ feeIDs: [fee.feeID] }} feeId={fee.feeID} /> : null}
                        </TabPane>
                    </TabContent>
                </Col>
            </Row>
            <YesNoModal title="Confirm Deletion" yesClick={deleteRecord} open={deletionConfirmationModal} toggle={toggleDeletionConfirmationModal}>Are you sure you want delete this Fee?</YesNoModal>
        </div>
    );
}

export default Fee;